import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs';
import { TuiScrollbar } from '@taiga-ui/core';
import { TuiScrollService } from '@taiga-ui/cdk';
import { Nullable, Optional } from '@src/types/utils';
import { BaseResizableComponent } from '@src/app/modules/base';

import { ApiService, StoreService, WebSocketService } from '../../services';
import { ChatModel, ChatPermissionsModel } from '../types';
import { MessageModel } from '../../message';
import { ConnectionState } from '../../types';

import { LOADING_OFFSET } from './constants';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ChatComponent extends BaseResizableComponent implements OnChanges {
  private readonly tuiScrollService = inject(TuiScrollService);
  private readonly api = inject(ApiService);
  private readonly store = inject(StoreService);
  private readonly router = inject(Router);
  private readonly wss = inject(WebSocketService);

  @Input() chatId?: string;
  chat?: ChatModel;

  @ViewChild(TuiScrollbar, { read: ElementRef }) private scrollBar?: ElementRef<HTMLElement>;

  visibleDetails = false;

  firstIncomingUnreadMessageId?: string;

  messages: Optional<Nullable<Array<MessageModel>>>;
  permissions: Optional<Nullable<ChatPermissionsModel>>;

  private firstLoading = true;

  readonly authUser$ = this.wss.authUser$;

  constructor() {
    super();

    effect(() => {
      if (this.wss.state() === ConnectionState.Connected) {
        this.openChat(this.chatId, this.wss.connectionId);
      }
    });
  }

  get hideChatInMobile() {
    return this.visibleDetails && this.isMobile;
  }

  get allMessagesOnScreen() {
    if (!this.scrollBar?.nativeElement) {
      return false;
    }

    const { clientHeight, scrollHeight } = this.scrollBar.nativeElement;
    return clientHeight === scrollHeight;
  }

  get scrollToBottomButtonVisible() {
    if (!this.scrollBar) {
      return false;
    }

    const { nativeElement } = this.scrollBar;
    return nativeElement.scrollTop <= nativeElement.scrollHeight - nativeElement.offsetHeight - 20;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chatId']) {
      this.firstLoading = true;
      const connectionId = this.wss.connectionId;
      const previousChatId = changes['chatId'].previousValue;

      this.closeChat(previousChatId, connectionId);

      if (this.chatId) {
        this.api.getChatFullInfo(this.chatId).subscribe();
        this.api.getChatPermissions(this.chatId).subscribe();
        this.api.getChatMessages(this.chatId)?.subscribe();

        this.openChat(this.chatId, connectionId);
      }
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.store.chats$.pipe(takeUntil(this.destroyed$$)).subscribe(chats => {
      if (this.chatId) {
        this.chat = chats[this.chatId];
      }
    });

    this.store.chatMessages$.pipe(takeUntil(this.destroyed$$)).subscribe(chatMessages => {
      this.messages =
        Object.keys(chatMessages).length && this.chatId && chatMessages[this.chatId]
          ? Object.values(chatMessages[this.chatId]).sort((a, b) => a.id.localeCompare(b.id))
          : [];

      if (this.firstLoading) {
        this.initScrollbar();

        setTimeout(() => {
          this.setFirstIncomingUnreadMessageId();

          if (this.firstIncomingUnreadMessageId) {
            this.scrollToMessage(this.firstIncomingUnreadMessageId);
          } else {
            this.scrollToBottom();
          }
          this.firstLoading = false;

          if (this.allMessagesOnScreen) {
            this.onScroll();
          }
        }, 300);
      } else if (!this.scrollToBottomButtonVisible) {
        setTimeout(() => {
          this.scrollToBottom();
        }, 0);
      }

      this.cdr.markForCheck();
    });

    this.store.chatPermissions$.pipe(takeUntil(this.destroyed$$)).subscribe(chatPermissions => {
      this.permissions =
        Object.keys(chatPermissions).length && this.chatId && chatPermissions[this.chatId]
          ? chatPermissions[this.chatId]
          : undefined;

      this.cdr.markForCheck();
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    const connectionId = this.wss.connectionId;

    this.closeChat(this.chatId, connectionId);
  }

  onDeleteChat() {
    if (!this.chatId) {
      return;
    }

    this.api.deleteChat(this.chatId).subscribe(() => {
      this.router.navigate(['/chats-inner']);
    });
  }

  onLeaveChat() {
    if (!this.chatId) {
      return;
    }

    this.api.leaveChat(this.chatId).subscribe(() => {
      this.router.navigate(['/chats-inner']);
    });
  }

  onMuteChange(isMute: boolean) {
    if (!this.chatId) {
      return;
    }

    this.api.setChatNotificationsSettings(this.chatId, isMute).subscribe();
  }

  initScrollbar() {
    if (!this.scrollBar || this.allMessagesOnScreen) {
      return;
    }

    this.scrollBar.nativeElement.scrollTop = LOADING_OFFSET;
  }

  onScroll() {
    if (!this.scrollBar) {
      return;
    }

    const { nativeElement } = this.scrollBar;

    if (nativeElement.scrollTop < LOADING_OFFSET && this.chatId) {
      this.api.getChatMessages(this.chatId, true)?.subscribe();
    }

    this.cdr.markForCheck();
  }

  scrollToMessage(messageId: string): void {
    const messageElement = document.getElementById(messageId);

    if (!this.scrollBar || !messageElement) {
      return;
    }

    this.tuiScrollService
      .scroll$(this.scrollBar.nativeElement, messageElement.offsetTop, 0, 0)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe();
  }

  scrollToBottom(): void {
    if (!this.scrollBar) {
      return;
    }

    const { nativeElement } = this.scrollBar;
    this.tuiScrollService
      .scroll$(nativeElement, nativeElement.scrollHeight, 0, 0)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe();
  }

  private setFirstIncomingUnreadMessageId() {
    if (!this.chat || !this.messages) {
      this.firstIncomingUnreadMessageId = undefined;
      return;
    }

    if (!this.chat?.lastReadMessageId) {
      this.firstIncomingUnreadMessageId = this.messages.find(
        message => message.userId !== this.authUser$.value?.id && message.id,
      )?.id;
      return;
    }

    this.firstIncomingUnreadMessageId = this.messages.find(message => {
      const isSelf = message.userId === this.authUser$.value?.id;
      return this.chat && this.chat.lastReadMessageId && !isSelf && message.id > this.chat.lastReadMessageId;
    })?.id;
  }

  private openChat(chatId?: string | null, connectionId?: string | null) {
    if (chatId && connectionId) {
      this.api.openChat(chatId, connectionId).subscribe(res => {
        this.store.updateChatsState([{ chatId: chatId, lastReadMessageId: res.lastReadMessageId }]);
      });
    }
  }

  private closeChat(chatId?: string | null, connectionId?: string | null) {
    if (chatId && connectionId) {
      this.api.closeChat(chatId, connectionId).subscribe();
    }
  }
}
